import { supabase } from '@/lib/supabase';
import { BRAND, ADDRESS_ONE_LINE, SUPPORT_WHATSAPP, Lang } from '@/data/constants';

/**
 * ───────────────────────────────────────────────────────────────────────────
 * EMAIL DELIVERY — single source of truth for the front end.
 * ───────────────────────────────────────────────────────────────────────────
 * Every surface that talks about how GigGone sends mail (the admin
 * deliverability panel, the dashboard notice feed, any future sender) reads
 * from this module. The actual rendering + sending happens server-side in the
 * `send-transactional-email` edge function; this file only describes the
 * contract so the UI and the function can never disagree about template names,
 * From identities or what counts as transactional.
 */

/** The ONLY two From identities allowed for account mail. Never send from anything else. */
export const EMAIL_SENDERS = {
  support: {
    key: 'support' as const,
    name: `${BRAND.name} Support`,
    address: 'support@giggone.com',
    use_en: 'Human replies, password resets — anything a person should reply to.',
    use_es: 'Respuestas humanas y restablecimiento de contraseña.',
  },
  notifications: {
    key: 'notifications' as const,
    name: `${BRAND.name} Notifications`,
    address: 'noreply@giggone.com',
    use_en: 'Automated account events: payments, activation, credits, lead unlocks.',
    use_es: 'Eventos automáticos: pagos, activación, créditos, desbloqueo de leads.',
  },
};

/** Identity block that MUST appear in the footer of every email (CAN-SPAM + trust). */
export const EMAIL_IDENTITY = {
  company: BRAND.fullName,
  address: ADDRESS_ONE_LINE,
  support: EMAIL_SENDERS.support.address,
  info: BRAND.email,
  whatsapp: SUPPORT_WHATSAPP.display,
  whatsappUrl: `https://wa.me/${SUPPORT_WHATSAPP.digits}`,
  website: 'https://giggone.com',
};

export type EmailTemplate =
  | 'payment_confirmation'
  | 'membership_activated'
  | 'account_activated'
  | 'password_reset'
  | 'credits_assigned'
  | 'lead_unlocked'
  | 'receipt'
  | 'support_reply';

export interface TemplateSpec {
  id: EmailTemplate;
  sender: keyof typeof EMAIL_SENDERS;
  label_en: string;
  label_es: string;
  /** Mirrored into the dashboard so the customer never depends on email delivery. */
  mirrored: boolean;
}

/** The eight critical emails, all strictly transactional (highest delivery priority). */
export const TRANSACTIONAL_TEMPLATES: TemplateSpec[] = [
  { id: 'payment_confirmation', sender: 'notifications', label_en: 'Payment confirmation', label_es: 'Confirmación de pago', mirrored: true },
  { id: 'membership_activated', sender: 'notifications', label_en: 'Membership activation', label_es: 'Activación de membresía', mirrored: true },
  { id: 'account_activated', sender: 'notifications', label_en: 'Account activation', label_es: 'Activación de cuenta', mirrored: true },
  { id: 'password_reset', sender: 'support', label_en: 'Password reset', label_es: 'Restablecer contraseña', mirrored: false },
  { id: 'credits_assigned', sender: 'notifications', label_en: 'Credit assignment', label_es: 'Asignación de créditos', mirrored: true },
  { id: 'lead_unlocked', sender: 'notifications', label_en: 'Lead unlock confirmation', label_es: 'Confirmación de lead desbloqueado', mirrored: true },
  { id: 'receipt', sender: 'notifications', label_en: 'Receipt', label_es: 'Recibo', mirrored: true },
  { id: 'support_reply', sender: 'support', label_en: 'Support reply', label_es: 'Respuesta de soporte', mirrored: true },
];

export const templateLabel = (id: string, lang: Lang): string => {
  const t = TRANSACTIONAL_TEMPLATES.find((x) => x.id === id);
  return t ? (lang === 'es' ? t.label_es : t.label_en) : id;
};

/** Content rules the server-side linter enforces on every send. */
export const CONTENT_RULES: { en: string; es: string }[] = [
  { en: 'No excessive capitalisation in the subject line', es: 'Sin mayúsculas excesivas en el asunto' },
  { en: 'One primary link, never more than six links total', es: 'Un enlace principal, nunca más de seis en total' },
  { en: 'At most one emoji, none in account mail', es: 'Máximo un emoji, ninguno en correo de cuenta' },
  { en: 'No sales language ("act now", "free money", "risk-free")', es: 'Sin lenguaje de venta agresivo' },
  { en: 'Never image-only — every email ships a text/plain part', es: 'Nunca solo imágenes — siempre versión de texto' },
  { en: 'No utm_/fbclid/gclid tracking parameters in account mail', es: 'Sin parámetros de rastreo en correo de cuenta' },
];

// ───────────────────────────────── API helpers ─────────────────────────────

export interface AuditCheck {
  status: 'pass' | 'warn' | 'fail';
  note: string;
  [k: string]: any;
}

export interface DeliverabilityAudit {
  success: boolean;
  domain: string;
  sendingDomain: string;
  checkedAt: string;
  overallScore: number;
  providerScores: { gmail: number; outlook: number; yahoo: number; apple: number };
  spf: AuditCheck;
  dkim: AuditCheck;
  dmarc: AuditCheck;
  alignment: AuditCheck;
  mx: AuditCheck;
  reputation: any;
  risks: { severity: string; title: string; detail: string }[];
  recommendations: string[];
  dnsChanges: { priority: string; type: string; host: string; value: string; why: string; done: boolean }[];
  senderDomainAuth: { domain: string; verified: boolean; dkimSelectors: string[]; spf: string[]; dmarc: string[]; note: string };
  error?: string;
}

/** Run a live SPF / DKIM / DMARC / alignment / reputation audit (admin only). */
export const runDeliverabilityAudit = async (persist = true): Promise<DeliverabilityAudit | { error: string }> => {
  const { data, error } = await supabase.functions.invoke('email-deliverability-audit', {
    body: { persist },
  });
  if (error) return { error: error.message };
  if (data?.error) return { error: data.error };
  return data as DeliverabilityAudit;
};

export interface SendTransactionalInput {
  template: EmailTemplate;
  to: string;
  language?: Lang;
  userId?: string | null;
  amountCents?: number;
  context?: Record<string, any>;
  dryRun?: boolean;
}

/**
 * Send one of the eight critical account emails. The edge function renders the
 * branded HTML + plain-text pair, refuses suppressed addresses, disables all
 * tracking and mirrors the notice into the dashboard.
 */
export const sendTransactionalEmail = async (input: SendTransactionalInput) => {
  const { data, error } = await supabase.functions.invoke('send-transactional-email', {
    body: {
      template: input.template,
      to: input.to,
      language: input.language || 'en',
      userId: input.userId || null,
      amountCents: input.amountCents,
      context: input.context || {},
      dryRun: input.dryRun === true,
    },
  });
  if (error) return { error: error.message };
  return data as any;
};

/** Public URL the email provider should POST delivery/bounce/complaint events to. */
export const emailWebhookUrl = (): string => {
  const base = (supabase as any)?.supabaseUrl || '';
  return `${base}/functions/v1/email-events-webhook`;
};

/** A dashboard-mirrored account notice (payment, activation, credits, lead unlock…). */
export interface AccountNotice {
  id: string;
  kind: string;
  title_en: string;
  title_es: string;
  body_en: string | null;
  body_es: string | null;
  amount: number | null;
  cta_path: string | null;
  read_at: string | null;
  created_at: string;
}

export const fetchAccountNotices = async (userId: string, limit = 8): Promise<AccountNotice[]> => {
  const { data } = await supabase
    .from('mkt_notifications')
    .select('id,kind,title_en,title_es,body_en,body_es,amount,cta_path,read_at,created_at')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(limit);
  return (data as AccountNotice[]) || [];
};

export const markNoticeRead = async (id: string) => {
  await supabase.from('mkt_notifications').update({ read_at: new Date().toISOString() }).eq('id', id);
};

export const scoreTone = (n: number): string =>
  n >= 85 ? 'text-emerald-600' : n >= 65 ? 'text-amber-600' : 'text-red-600';

export const statusTone = (s?: string): string =>
  s === 'pass'
    ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
    : s === 'warn'
      ? 'bg-amber-50 text-amber-700 ring-amber-200'
      : 'bg-red-50 text-red-700 ring-red-200';

export const severityTone = (s: string): string =>
  s === 'critical'
    ? 'bg-red-50 text-red-700 ring-red-200'
    : s === 'high'
      ? 'bg-orange-50 text-orange-700 ring-orange-200'
      : s === 'medium'
        ? 'bg-amber-50 text-amber-700 ring-amber-200'
        : 'bg-slate-50 text-slate-600 ring-slate-200';
